import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * EXTRACT-UNITS-1 — the "is this statement in thousands?" gate.
 *
 * A statement headed "(in thousands)" is the largest single error this pipeline
 * can make: read it literally and every figure is 1,000x too small; scale it
 * without saying so and the CPA signs a working paper whose numbers appear
 * nowhere on the source document. Neither is allowed to happen quietly, so the
 * whole document is held out of the grid until a named human answers.
 *
 * Held here means held: no partial load. The unit is a property of the
 * statement, not of one column, so admitting half of it would put dollars and
 * thousands in the same grid — the one outcome worse than asking.
 *
 * Its own component rather than more markup inside ExtractionPanel, which is
 * past the 400-line split threshold (FILE_SIZE_POLICY.md).
 */

/** A document whose printed unit has not been confirmed. Not in the grid. */
export interface HeldScale {
  doc_id: string;
  filename: string;
  factor: number;
  scale_name: string;
  /** "declared" — the document says so; "inferred" — the magnitudes suggest it. */
  basis?: string;
  /** The words on the page, or the magnitude finding, that raised the question. */
  evidence?: string;
  /** True when a reader already multiplied — declining has to undo that. */
  applied_by_reader?: boolean;
  prompt?: string;
  reason: string;
  periods?: { period_label: string; year: number | null; fields: Record<string, number> }[];
}

/** A document whose unit a CPA has already ruled on, and what that ruling did. */
export interface ResolvedScale {
  doc_id: string;
  filename: string;
  decision: string;
  factor: number;
  scale_name: string;
  applied_by_reader?: boolean;
  adjustment?: number;
  note?: string;
}

interface Props {
  held: HeldScale[];
  resolved: ResolvedScale[];
  busy: boolean;
  running: boolean;
  /**
   * Passed in rather than imported so this file does not import back from
   * ExtractionPanel, which imports it — the field labels and the currency both
   * live there and there is no reason for two copies of either.
   */
  summarize: (fields: Record<string, number> | undefined) => string;
  onConfirm: (docId: string) => void;
  onDecline: (docId: string) => void;
}

/** "in thousands" reads better mid-sentence than the bare scale name. */
const unitPhrase = (name: string): string =>
  name && name !== "whole dollars" ? `in ${name}` : "in whole dollars";

export const ScaleNotice: React.FC<Props> = ({
  held, resolved, busy, running, summarize, onConfirm, onDecline,
}) => {
  if (held.length === 0 && resolved.length === 0) return null;

  return (
    <>
      {held.length > 0 && (
        <div
          className="mt-2 rounded-lg border border-amber-500/60 bg-amber-500/5 p-2"
          data-testid="scale-held"
        >
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
            <span>
              {held.length === 1
                ? "1 document is not in whole dollars — confirm before it goes into the grid"
                : `${held.length} documents are not in whole dollars — confirm before they go into the grid`}
            </span>
          </p>
          <ul className="mt-1.5 space-y-2">
            {held.map(h => (
              <li key={h.doc_id} className="text-xs">
                <span className="text-brand-text">
                  {h.filename || "An uploaded document"} appears to be printed{" "}
                  {unitPhrase(h.scale_name)}
                  {h.evidence ? ` (${h.evidence})` : ""}.
                  {h.applied_by_reader
                    ? " Its figures have already been multiplied by " +
                      `${h.factor.toLocaleString()} — confirm to keep that, or decline to undo it.`
                    : ` Confirming multiplies its figures by ${h.factor.toLocaleString()}.`}
                </span>
                <span className="mt-0.5 block text-muted-foreground">{h.reason}</span>
                {(h.periods ?? []).slice(0, 3).map((p, i) => (
                  <span
                    key={`${h.doc_id}-${p.period_label}-${i}`}
                    className="mt-0.5 block text-muted-foreground"
                  >
                    {p.period_label || (p.year ? `FY${p.year}` : "One period")} as
                    printed: {summarize(p.fields)}
                  </span>
                ))}
                <span className="mt-1 flex flex-wrap gap-2">
                  <Button
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => onConfirm(h.doc_id)}
                  >
                    {running && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                    Yes — figures are {unitPhrase(h.scale_name)}
                  </Button>
                  <Button
                    size="sm" variant="ghost" disabled={busy}
                    onClick={() => onDecline(h.doc_id)}
                  >
                    No — use the figures exactly as printed
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {resolved.length > 0 && (
        <details
          className="mt-2 rounded-lg border border-border/60 p-2 text-xs"
          data-testid="scale-resolved"
        >
          <summary className="cursor-pointer font-medium text-brand-text">
            {resolved.length} document{resolved.length === 1 ? "" : "s"} you have
            confirmed the units for
          </summary>
          <ul className="mt-1 space-y-1.5">
            {resolved.map(r => (
              <li key={r.doc_id} className="text-muted-foreground">
                <span className="text-brand-text">{r.filename || "Document"}</span>
                {" — "}
                {r.note ||
                  (r.decision === "confirmed"
                    ? `read ${unitPhrase(r.scale_name)}.`
                    : "used exactly as printed.")}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
};

export default ScaleNotice;
